import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, ChevronLeft, ChevronRight, Plus, X } from "lucide-react";

// Plain-English intents → calc/input recipe
const INTENTS = [
  { id: "core_fixed", label: "A set fee whenever this service is selected",
    hint: "e.g. a base monthly fee of £39.50 that always applies.",
    calc_type: "core_fixed", input_type: "automatic", period: "monthly", uses: "rate" },
  { id: "yes_no_fixed", label: "A set fee when the client answers Yes",
    hint: "e.g. Payroll setup: Yes = £75, No = £0.",
    calc_type: "conditional_fixed", input_type: "yes_no", period: "monthly", uses: "rate" },
  { id: "qty_rate", label: "A price per unit (quantity × rate)",
    hint: "e.g. 3 reconciliations × £25 = £75.",
    calc_type: "qty_rate", input_type: "number", period: "monthly", uses: "rate" },
  { id: "qty_over_allowance", label: "A price per unit above an included allowance",
    hint: "e.g. first 5 employees free, then £1.50 each after.",
    calc_type: "qty_over_allowance", input_type: "number", period: "monthly", uses: "rate_allowance" },
  { id: "band", label: "Pick a price from a volume band",
    hint: "e.g. 0–25 txns = £20, 26–50 = £35, 51–100 = £55.",
    calc_type: "option_amount", input_type: "dropdown", period: "monthly", uses: "bands" },
  { id: "options", label: "Pick a price from a list of options",
    hint: "e.g. a plan or add-on chosen from a dropdown.",
    calc_type: "option_amount", input_type: "dropdown", period: "monthly", uses: "options" },
  { id: "multiplier", label: "Multiply the price by a chosen factor",
    hint: "e.g. Complexity: Simple ×0.9, Standard ×1.0, Complex ×1.2.",
    calc_type: "multiplier", input_type: "dropdown", period: "monthly", uses: "multiplier" },
  { id: "reduce_pct", label: "Reduce the price by a percentage (discount)",
    hint: "e.g. 'CASS doing the bookkeeping?' Yes → −20%.",
    calc_type: "reduce_pct", input_type: "yes_no", period: "monthly", uses: "pct" },
  { id: "increase_pct", label: "Increase the price by a percentage (surcharge)",
    hint: "e.g. a rush/complexity uplift of +15%.",
    calc_type: "increase_pct", input_type: "yes_no", period: "monthly", uses: "pct" },
  { id: "manual", label: "A manual one-off amount entered per proposal",
    hint: "e.g. a bespoke one-off fee typed in when building the proposal.",
    calc_type: "manual_adjust", input_type: "currency", period: "one_off", uses: "none" },
];

const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 28);

function suggestPrefix(existingKeys, internalName) {
  const counts = {};
  existingKeys.forEach((k) => {
    const p = String(k).split("_")[0];
    if (p && p.length <= 6) counts[p] = (counts[p] || 0) + 1;
  });
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  if (top) return top[0];
  return slug(internalName).split("_")[0].slice(0, 4) || "cmp";
}

function uniqueKey(base, existingKeys) {
  let k = base || "component";
  if (!existingKeys.includes(k)) return k;
  let i = 2;
  while (existingKeys.includes(`${k}_${i}`)) i++;
  return `${k}_${i}`;
}

export default function ComponentWizard({ existingKeys = [], internalName = "", onClose, onCreate }) {
  const [step, setStep] = useState(0);
  const [intent, setIntent] = useState(null);
  const [label, setLabel] = useState("");
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [group, setGroup] = useState("");
  const [rate, setRate] = useState(0);
  const [allowance, setAllowance] = useState(0);
  const [period, setPeriod] = useState("monthly");
  const [options, setOptions] = useState([]);

  const prefix = useMemo(() => suggestPrefix(existingKeys, internalName), [existingKeys, internalName]);
  const suggestedKey = useMemo(
    () => uniqueKey(`${prefix}_${slug(label) || "component"}`, existingKeys),
    [prefix, label, existingKeys]
  );
  const effectiveKey = keyTouched ? key.trim() : suggestedKey;
  const keyDup = existingKeys.includes(effectiveKey);
  const keyValid = /^[a-z][a-z0-9_]*$/.test(effectiveKey) && !keyDup;

  const uses = intent?.uses;
  const isMult = uses === "multiplier";
  const isBandsOrOptions = uses === "bands" || uses === "options";

  const addOpt = () =>
    setOptions([...options, { key: "", name: "", value: 0, value_type: isMult ? "×" : "£", from: "", to: "" }]);
  const setOpt = (i, patch) => { const o = [...options]; o[i] = { ...o[i], ...patch }; setOptions(o); };
  const delOpt = (i) => setOptions(options.filter((_, x) => x !== i));

  const buildComponent = () => {
    const base = {
      key: effectiveKey, name: label || effectiveKey, label: label || effectiveKey, group: group || "General",
      help_text: "", input_type: intent.input_type, calc_type: intent.calc_type, rate: 0, required: false,
      default: "", order: 999, active: true, period, applies_to: "entire_service", allowance: 0,
      options: [], condition: null, depends_on: "",
    };
    if (uses === "rate") base.rate = rate;
    if (uses === "rate_allowance") { base.rate = rate; base.allowance = allowance; }
    if (uses === "pct") base.rate = rate;
    if (isBandsOrOptions || isMult) {
      base.options = options.map((o, i) => {
        const name = o.name || (uses === "bands" ? `${o.from || 0}–${o.to || ""}` : `Option ${i + 1}`);
        return {
          key: o.key || slug(name) || `opt_${i + 1}`, name,
          value: parseFloat(o.value) || 0, value_type: isMult ? "×" : "£",
          order: i + 1, active: true,
          ...(uses === "bands" ? { from: o.from === "" ? undefined : Number(o.from), to: o.to === "" ? undefined : Number(o.to) } : {}),
        };
      });
    }
    return base;
  };

  const canNext = () => {
    if (step === 0) return !!intent;
    if (step === 1) return !!label.trim() && keyValid;
    if (step === 2) {
      if (isBandsOrOptions || isMult) return options.length > 0 && options.every((o) => o.name?.trim() || (o.from !== "" && o.to !== ""));
      return true;
    }
    return true;
  };

  const next = () => setStep((s) => Math.min(s + 1, 3));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const finish = () => { onCreate(buildComponent()); onClose(); };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="component-wizard">
        <DialogHeader><DialogTitle>Create pricing component — step {step + 1} of 4</DialogTitle></DialogHeader>

        {/* progress */}
        <div className="flex gap-1.5 mb-1" data-testid="wizard-progress">
          {["What it does", "Name & key", "Configure", "Review"].map((t, i) => (
            <div key={t} className={`flex-1 text-center text-[11px] py-1 rounded ${i === step ? "bg-cass-navy text-white" : i < step ? "bg-cass-teal/15 text-cass-teal" : "bg-slate-100 text-cass-slate"}`}>
              {i < step ? <Check className="w-3 h-3 inline mr-0.5" /> : `${i + 1}. `}{t}
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-2" data-testid="wizard-step-intent">
            <p className="text-sm text-cass-slate">What should this component do? Pick the plain-English description that fits.</p>
            {INTENTS.map((it) => (
              <Card key={it.id} onClick={() => setIntent(it)}
                className={`p-3 cursor-pointer transition-colors ${intent?.id === it.id ? "border-cass-teal ring-1 ring-cass-teal bg-cass-teal/5" : "hover:border-cass-slate/40"}`}
                data-testid={`intent-${it.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-cass-navy text-sm">{it.label}</span>
                  {intent?.id === it.id && <Check className="w-4 h-4 text-cass-teal" />}
                </div>
                <p className="text-xs text-cass-slate mt-0.5">{it.hint}</p>
              </Card>
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3" data-testid="wizard-step-name">
            <div>
              <Label className="text-xs">Proposal question / label</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Number of bank accounts to reconcile" data-testid="wizard-label" autoFocus />
            </div>
            <div>
              <Label className="text-xs">Display group (optional)</Label>
              <Input value={group} onChange={(e) => setGroup(e.target.value)} placeholder="e.g. Bookkeeping" data-testid="wizard-group" />
            </div>
            <div>
              <Label className="text-xs">Component key (must be unique)</Label>
              <Input value={effectiveKey} onChange={(e) => { setKeyTouched(true); setKey(e.target.value); }} data-testid="wizard-key" />
              {!keyTouched && <p className="text-[11px] text-cass-teal mt-1">Auto-suggested from the label. Edit if you prefer.</p>}
              {keyTouched && keyDup && <p className="text-[11px] text-red-600 mt-1">That key is already used on this service.</p>}
              {keyTouched && !keyDup && !keyValid && <p className="text-[11px] text-red-600 mt-1">Use lowercase letters, numbers and underscores; start with a letter.</p>}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-config">
            {uses === "rate" && (
              <div><Label className="text-xs">Amount (£)</Label>
                <Input type="number" value={rate} onChange={(e) => setRate(parseFloat(e.target.value) || 0)} data-testid="wizard-rate" /></div>
            )}
            {uses === "pct" && (
              <div><Label className="text-xs">Percentage (e.g. 20 for {intent.id === "reduce_pct" ? "−" : "+"}20%)</Label>
                <Input type="number" value={rate} onChange={(e) => setRate(parseFloat(e.target.value) || 0)} data-testid="wizard-rate" /></div>
            )}
            {uses === "rate_allowance" && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Rate per unit (£)</Label>
                  <Input type="number" value={rate} onChange={(e) => setRate(parseFloat(e.target.value) || 0)} data-testid="wizard-rate" /></div>
                <div><Label className="text-xs">Included allowance (free units)</Label>
                  <Input type="number" value={allowance} onChange={(e) => setAllowance(parseFloat(e.target.value) || 0)} data-testid="wizard-allowance" /></div>
              </div>
            )}
            {(isBandsOrOptions || isMult) && (
              <div>
                <div className="flex justify-between items-center mb-1">
                  <Label className="text-xs">{uses === "bands" ? "Bands (From / To / Label / Price)" : isMult ? "Options (factor)" : "Options (£ each)"}</Label>
                  <Button size="sm" variant="outline" onClick={addOpt} data-testid="wizard-add-option"><Plus className="w-3 h-3 mr-1" />Add</Button>
                </div>
                {options.length === 0 && <p className="text-[11px] text-cass-slate">Add at least one {uses === "bands" ? "band" : "option"}.</p>}
                {options.map((o, i) => (
                  <div key={i} className="flex gap-1.5 mb-1 items-center" data-testid={`wizard-option-${i}`}>
                    {uses === "bands" && <>
                      <Input className="w-16" type="number" placeholder="From" value={o.from} onChange={(e) => setOpt(i, { from: e.target.value })} />
                      <Input className="w-16" type="number" placeholder="To" value={o.to} onChange={(e) => setOpt(i, { to: e.target.value })} />
                    </>}
                    <Input className="flex-1 min-w-0" placeholder={uses === "bands" ? "Label (optional)" : "Option name"} value={o.name} onChange={(e) => setOpt(i, { name: e.target.value })} />
                    <Input className="w-20" type="number" placeholder={isMult ? "×" : "£"} value={o.value} onChange={(e) => setOpt(i, { value: e.target.value })} />
                    <Button size="icon" variant="ghost" onClick={() => delOpt(i)}><X className="w-4 h-4" /></Button>
                  </div>
                ))}
                {uses === "bands" && options.length > 1 && bandsOverlap(options) && (
                  <p className="text-[11px] text-red-600 mt-1" data-testid="wizard-band-overlap">⚠ Some bands overlap — check the From/To ranges.</p>
                )}
              </div>
            )}
            {uses === "none" && <p className="text-sm text-cass-slate">Nothing to configure — this is a manual amount entered when building each proposal.</p>}
            <div>
              <Label className="text-xs">Pricing period</Label>
              <Select value={period} onValueChange={setPeriod}><SelectTrigger data-testid="wizard-period"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="monthly">Monthly (recurring)</SelectItem><SelectItem value="one_off">One-off</SelectItem></SelectContent></Select>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-2 text-sm" data-testid="wizard-step-review">
            <p className="text-cass-slate">Review the component. You can fine-tune anything in the full editor after adding.</p>
            <Card className="p-3 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-cass-navy">{label}</span>
                <Badge variant="outline" className="text-[10px]">{intent.calc_type}</Badge>
                <Badge variant="outline" className="text-[10px]">{period}</Badge>
              </div>
              <p className="text-xs text-cass-slate">key: <b>{effectiveKey}</b> · input: {intent.input_type}{group ? ` · group: ${group}` : ""}</p>
              {uses === "rate" && <p className="text-xs">Amount: £{rate}</p>}
              {uses === "pct" && <p className="text-xs">{intent.id === "reduce_pct" ? "Reduce" : "Increase"} by {rate}%</p>}
              {uses === "rate_allowance" && <p className="text-xs">£{rate}/unit above {allowance} included</p>}
              {(isBandsOrOptions || isMult) && (
                <ul className="text-xs list-disc pl-4">
                  {options.map((o, i) => (
                    <li key={i}>{uses === "bands" ? `${o.from || 0}–${o.to || "∞"}: ` : `${o.name || `Option ${i + 1}`}: `}{isMult ? `×${o.value}` : `£${o.value}`}</li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        )}

        <DialogFooter className="flex sm:justify-between gap-2">
          <Button variant="ghost" onClick={step === 0 ? onClose : back} data-testid="wizard-back">
            {step === 0 ? "Cancel" : <><ChevronLeft className="w-4 h-4 mr-1" />Back</>}
          </Button>
          {step < 3 ? (
            <Button className="bg-cass-navy text-white" onClick={next} disabled={!canNext()} data-testid="wizard-next">Next<ChevronRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <Button className="bg-cass-teal text-white" onClick={finish} data-testid="wizard-finish"><Check className="w-4 h-4 mr-1" />Add component</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function bandsOverlap(options) {
  const rows = options
    .map((o) => ({ from: o.from === "" ? null : Number(o.from), to: o.to === "" ? null : Number(o.to) }))
    .filter((r) => r.from != null && r.to != null)
    .sort((a, b) => a.from - b.from);
  for (let i = 1; i < rows.length; i++) if (rows[i].from <= rows[i - 1].to) return true;
  return false;
}
